import React, { useState, useEffect, useRef } from 'react';
import AppStoreButtons from './AppStoreButtons';
import { useTranslation } from 'react-i18next';

// Animation styles
const footerBannerAnimations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInLeft {
    from {
      opacity: 0;
      transform: translateX(-50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes imageFloat {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-15px);
    }
  }
`;

function FooterBanner() {
	const { t } = useTranslation();
	const bannerRef = useRef(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const options = {
			root: null,
			rootMargin: '0px',
			threshold: 0.25, // Trigger when 15% of the element is visible
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					// Once animated, no need to observe anymore
					observer.disconnect();
				}
			});
		}, options);

		// Start observing the section
		if (bannerRef.current) {
			observer.observe(bannerRef.current);
		}

		return () => {
			if (bannerRef.current) {
				observer.unobserve(bannerRef.current);
			}
		};
	}, []);

	return (
		<>
			<style>{footerBannerAnimations}</style>
			<div ref={bannerRef} className="lg:min-h-[570px] flex items-center lg:justify-center bg-pink w-full py-[22px] px-[40px] xl:px-0 overflow-hidden">
				<div className="flex flex-col lg:flex-row items-center h-full justify-center xl:gap-[100px] md:gap-[50px] transition-[gap] ease-in-out">
					<div className="flex-2 flex flex-col gap-[30px] w-full xl:max-w-[566px] justify-center">
						<h2
							className="font-bold text-[46px] leading-[54px]"
							style={{
								opacity: isVisible ? 1 : 0,
								transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
								transition: 'opacity 0.7s ease, transform 0.7s ease',
							}}
						>
							{t('Create your products with our application.')}
						</h2>

						<p
							className="lg:pr-[90px] text-[14px]"
							style={{
								opacity: isVisible ? 1 : 0,
								transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
								transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
							}}
						>
							{t('Dive into the realm of creativity with our innovative application that puts the power of design into your hands! Unleash your imagination and craft personalized gifts that reflect your unique style.')}
						</p>

						<p
							className="lg:pr-[90px] text-[14px]"
							style={{
								opacity: isVisible ? 1 : 0,
								transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
								transition: 'opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s',
							}}
						>
							{t('footer_banner_last_section')}
						</p>

						<div
							className="flex flex-row items-center gap-[10px]"
							style={{
								opacity: isVisible ? 1 : 0,
								transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
								transition: 'opacity 0.7s ease 0.6s, transform 0.7s ease 0.6s',
							}}
						>
							<AppStoreButtons />
						</div>
					</div>

					<div
						className="flex items-center justify-center flex-0 mt-8 lg:mt-0"
						style={{
							opacity: isVisible ? 1 : 0,
							transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
							transition: 'opacity 0.9s ease 0.5s, transform 0.9s ease 0.5s',
						}}
					>
						<img
							src={require('../assets/images/FooterBanner.webp')}
							loading="lazy"
							alt="Footer"
							className="h-[100%] self-center max-h-[526px]"
							title="Footer Banner"
							style={{
								animation: isVisible ? 'imageFloat 6s ease-in-out infinite' : 'none',
								animationDelay: '1s',
							}}
						/>
					</div>
				</div>
			</div>
		</>
	);
}

export default FooterBanner;
